import { useEffect, useState } from "react";
import { mobileDigits } from "@/lib/phone";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Ban, RotateCcw } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const empty = { business_name: "", owner_name: "", phone: "", email: "", bio: "", service_area: "", home_visit: true, center_visit: false, avatar_url: "" };

export default function TAdminSellers() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/tenant/sellers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/tenant/sellers", form);
      else await api.put(`/tenant/sellers/${editing}`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save the seller");
    }
  };

  // Suspending closes the seller portal to that seller at once (they are signed
  // out and cannot sign in or act on a job) and takes them off the booking
  // screen. Their jobs stay with them: reassign those in Bookings. Reinstating
  // opens everything again. The server enforces all of it.
  const setStatus = async (s, status) => {
    const question = status === "suspended"
      ? `Suspend ${s.business_name}? They are signed out and cannot sign in or work on their jobs until reinstated.`
      : `Reinstate ${s.business_name}? They can sign in and take jobs again.`;
    if (!window.confirm(question)) return;
    try {
      await api.put(`/tenant/sellers/${s.id}`, { status });
      toast.success(status === "suspended" ? `${s.business_name} is suspended` : `${s.business_name} is reinstated`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "The seller could not be changed");
    }
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this seller?")) return;
    await api.delete(`/tenant/sellers/${id}`); toast.success("Deleted"); load();
  };

  const edit = (s) => { setEditing(s.id); setForm({ ...empty, ...s }); window.scrollTo({ top: 0, behavior: "smooth" }); };   // the form is at the top of the page
  const statusClass = (s) => `text-xs uppercase font-semibold ${s.status === "approved" ? "text-green-700" : "text-red-700"}`;

  return (
    <div data-testid="tadmin-sellers">
      <div className="flex flex-wrap justify-between items-end gap-3 mb-6">
        <div><div className="admin-overline">Network</div><h1 className="admin-heading text-3xl font-bold mt-1">Sellers / Vendors</h1></div>
        <button onClick={() => { setEditing("new"); setForm(empty); }} className="bg-black text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2" data-testid="tadmin-seller-new-btn">
          <Plus className="h-3.5 w-3.5" /> New seller
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white border border-neutral-200 p-4 sm:p-6 mb-6 grid md:grid-cols-2 gap-4">
          <div><label className="admin-overline">Business name</label><input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" data-testid="tadmin-seller-form-name" /></div>
          <div><label className="admin-overline">Owner name</label><input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" /></div>
          <div><label className="admin-overline">Mobile number (10 digits, used to sign in)</label><input type="tel" inputMode="numeric" required pattern="[6-9][0-9]{9}" title="10-digit mobile number, without +91" value={mobileDigits(form.phone)} onChange={(e) => setForm({ ...form, phone: mobileDigits(e.target.value) })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" data-testid="tadmin-seller-form-phone" />
            <div className="text-[11px] text-neutral-500 mt-1">The seller signs in to the seller portal with this number.</div></div>
          <div><label className="admin-overline">Email</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" /></div>
          <div><label className="admin-overline">Service area</label><input value={form.service_area} onChange={(e) => setForm({ ...form, service_area: e.target.value })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" /></div>
          <div>
            <ImageUploader label="Avatar" value={form.avatar_url} onChange={(url) => setForm({ ...form, avatar_url: url })} folder="sellers" shape="round" testid="tadmin-seller-form-avatar" />
          </div>
          <div className="md:col-span-2"><label className="admin-overline">Bio</label><textarea rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" /></div>
          <div className="md:col-span-2 flex gap-2">
            <button type="submit" className="bg-black text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider" data-testid="tadmin-seller-form-save">Save</button>
            <button type="button" onClick={() => setEditing(null)} className="border border-neutral-300 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider">Cancel</button>
          </div>
        </form>
      )}

      {/* Phones: one card per seller, with its actions in view (the table would hide them off to the side) */}
      <div className="md:hidden space-y-3" data-testid="tadmin-sellers-cards">
        {list.length === 0 && <div className="bg-white border border-neutral-200 py-8 text-center text-neutral-500 text-sm">No sellers yet.</div>}
        {list.map((s) => (
          <div key={s.id} className="bg-white border border-neutral-200 p-4" data-testid={`tadmin-seller-card-${s.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="font-semibold break-words">{s.business_name}</div>
                <div className="text-xs text-neutral-600 break-words mt-0.5">{[s.owner_name, s.phone].filter(Boolean).join(" · ")}</div>
                <div className="text-xs text-neutral-500 mt-0.5">★ {s.rating?.toFixed(1)} ({s.total_jobs})</div>
              </div>
              <span className={`${statusClass(s)} flex-shrink-0`} data-testid={`tadmin-seller-card-status-${s.id}`}>{s.status}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <button type="button" onClick={() => edit(s)} className="border border-neutral-300 text-xs font-semibold uppercase tracking-wider min-h-[44px] inline-flex items-center justify-center gap-1.5" data-testid={`tadmin-seller-card-edit-${s.id}`}>
                <Edit2 className="h-3.5 w-3.5" /> Edit
              </button>
              {s.status === "approved"
                ? <button type="button" onClick={() => setStatus(s, "suspended")} className="border border-red-300 text-red-700 text-xs font-semibold uppercase tracking-wider min-h-[44px] inline-flex items-center justify-center gap-1.5" data-testid={`tadmin-seller-card-suspend-${s.id}`}><Ban className="h-3.5 w-3.5" /> Suspend</button>
                : <button type="button" onClick={() => setStatus(s, "approved")} className="border border-green-300 text-green-700 text-xs font-semibold uppercase tracking-wider min-h-[44px] inline-flex items-center justify-center gap-1.5" data-testid={`tadmin-seller-card-reinstate-${s.id}`}><RotateCcw className="h-3.5 w-3.5" /> Reinstate</button>}
              <button type="button" onClick={() => remove(s.id)} className="border border-neutral-300 text-red-700 text-xs font-semibold uppercase tracking-wider min-h-[44px] inline-flex items-center justify-center gap-1.5" data-testid={`tadmin-seller-card-delete-${s.id}`}>
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block bg-white border border-neutral-200 table-scroll" data-testid="tadmin-sellers-table">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-50"><tr className="text-left">
            <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Business</th>
            <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Owner</th>
            <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Phone</th>
            <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Rating</th>
            <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Status</th>
            <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="px-4 py-3 font-semibold">{s.business_name}</td>
                <td className="px-4 py-3 text-xs">{s.owner_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.phone}</td>
                <td className="px-4 py-3">{s.rating?.toFixed(1)} ({s.total_jobs})</td>
                <td className="px-4 py-3"><span className={statusClass(s)} data-testid={`tadmin-seller-status-${s.id}`}>{s.status}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {s.status === "approved"
                    ? <button onClick={() => setStatus(s, "suspended")} className="icon-btn hover:bg-red-50 text-red-700" aria-label={`Suspend ${s.business_name}`} title="Suspend" data-testid={`tadmin-seller-suspend-${s.id}`}><Ban className="h-3.5 w-3.5" /></button>
                    : <button onClick={() => setStatus(s, "approved")} className="icon-btn hover:bg-green-50 text-green-700" aria-label={`Reinstate ${s.business_name}`} title="Reinstate" data-testid={`tadmin-seller-reinstate-${s.id}`}><RotateCcw className="h-3.5 w-3.5" /></button>}
                  <button onClick={() => edit(s)} className="icon-btn hover:bg-neutral-100" aria-label="Edit"><Edit2 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(s.id)} className="icon-btn hover:bg-red-50 text-red-700" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
